import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Dialog } from "@mui/material";
import { Box, Typography, Divider } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CustomScrollbar from "./CustomScrollbar";
import designTokens from "../styles/designTokens";

interface PrivacyProps {
  open?: boolean;
  onClose?: () => void;
}

const Privacy: React.FC<PrivacyProps> = ({
  open: openProp,
  onClose: onCloseProp,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // props가 있으면 props 사용, 없으면 URL 기반
  const open =
    openProp !== undefined ? openProp : location.pathname === "/privacy";

  const handleClose = (): void => {
    if (onCloseProp) {
      onCloseProp();
    } else {
      navigate(-1);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: designTokens.fill.shape.grey30.dark,
          backdropFilter: `blur(${designTokens.numbers.effects[16]})`,
          WebkitBackdropFilter: `blur(${designTokens.numbers.effects[16]})`,
          boxShadow: `0px 0px ${designTokens.numbers.effects[4]} 0px ${designTokens.effects.blackOpacity10.dark}, 0px 1px 8px 0px rgba(0,0,0,0.12)`,
          color: designTokens.fill.text.grey90.dark,
          borderRadius: {
            xs: designTokens.numbers.cornerRadius[16],
            sm: designTokens.numbers.cornerRadius[24],
            md: designTokens.numbers.cornerRadius[32],
          },
          maxWidth: { xs: "100%", sm: "600px", md: "900px", lg: "1080px" },
          maxHeight: { xs: "100vh", sm: "90vh", md: "720px" },
          width: "100%",
          height: { xs: "100vh", sm: "auto" },
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          transition: "none",
          animation: "none",
          transform: "none",
          m: { xs: 0, sm: "auto" },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: {
              xs: designTokens.numbers.cornerRadius[16],
              sm: designTokens.numbers.cornerRadius[24],
              md: designTokens.numbers.cornerRadius[32],
            },
            pointerEvents: "none",
            boxShadow: `
              1px 1px 0px -0.5px inset ${designTokens.fill.shape.grey30.dark},
              -1px -1px 0px -0.5px inset ${designTokens.fill.shape.grey30.dark},
              1px 1px 0.5px -1px inset ${designTokens.fill.shape.common100.dark},
              -1px -1px 0.5px -1px inset ${designTokens.fill.shape.common100.dark},
              0px 0px 3px 0px inset ${designTokens.fill.shape.common100.dark},
              0px 0px 16px 0px inset ${designTokens.fill.shape.grey30.dark}
            `,
          },
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: designTokens.fill.background.dim.dark,
        },
      }}
      fullScreen={false}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Top Spacer */}
        <Box
          sx={{
            height: {
              xs: designTokens.numbers.gap[16],
              sm: designTokens.numbers.gap[24],
              md: designTokens.numbers.gap[32],
            },
            flexShrink: 0,
          }}
        />

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: {
              xs: designTokens.numbers.gap[12],
              sm: designTokens.numbers.gap[16],
            },
            px: {
              xs: designTokens.numbers.gap[16],
              sm: designTokens.numbers.gap[24],
              md: designTokens.numbers.gap[32],
            },
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <Button
              sx={{
                color: designTokens.fill.text.grey90.dark,
                minWidth: "24px",
                width: "24px",
                height: "24px",
                p: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={handleClose}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: "15px" }} />
            </Button>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  ...designTokens.typography.styles.heading,
                  color: designTokens.fill.text.grey90.dark,
                }}
              >
                View R Privacy Policy
              </Typography>
              <Typography
                sx={{
                  ...designTokens.typography.styles.caption,
                  color: designTokens.fill.text.grey60.dark,
                }}
              >
                Last updated November 03, 2025
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ borderColor: designTokens.stroke.grey40.dark }} />
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            display: "flex",
            flexShrink: 1,
            px: {
              xs: designTokens.numbers.gap[16],
              sm: designTokens.numbers.gap[24],
              md: designTokens.numbers.gap[32],
            },
            pt: {
              xs: designTokens.numbers.gap[12],
              sm: designTokens.numbers.gap[16],
            },
            pb: 0,
          }}
        >
          <CustomScrollbar
            height="100%"
            width="100%"
            thumbHeight={designTokens.numbers.gap[114]}
            borderRadius={designTokens.numbers.cornerRadius[0]}
          >
            <Typography
              variant="body1"
              sx={{
                ...designTokens.typography.styles.label2,
                color: designTokens.fill.text.grey90.dark,
                textAlign: "left",
                whiteSpace: "pre-line",
                fontSize: { xs: "13px", sm: "14px", md: "15px" },
                lineHeight: { xs: 1.5, sm: 1.6 },
              }}
            >
              This Privacy Notice for Byond R Inc. (doing business as View R) ("we," "us," or "our"), describes how and why we might access, collect, store, use, and/or share ("process") your personal information when you use our services ("Services"), including when you:
              <br />
              <br />
              ● Visit our website at http://www.viewr.co or any website of ours that links to this Privacy Notice
              <br />
              <br />
              ● Use View R. View R is a platform for organizing, viewing, and sharing 3D models and related content in an interactive workspace. It supports both extended reality (XR) environments (AR/VR) and standard desktop access, enabling users to present, review, or document 3D assets with or without immersive hardware. Typical use cases include design reviews, technical documentation, training materials, and collaborative product development.
              <br />
              <br />
              ● Engage with us in other related ways, including any sales, marketing, or events
              <br />
              <br />
              Questions or concerns? Reading this Privacy Notice will help you understand your privacy rights and choices. We are responsible for making decisions about how your personal information is processed. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at sales@byond-r.com.
              <br />
              <br />
              <strong>SUMMARY OF KEY POINTS</strong>
              <br />
              <br />
              This summary provides key points from our Privacy Notice, but you can find out more details about any of these topics by clicking the link following each key point or by using our table of contents below to find the section you are looking for.
              <br />
              <br />
              What personal information do we process? When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use.
              <br />
              <br />
              Do we process any sensitive personal information? We do not process sensitive personal information.
              <br />
              <br />
              Do we collect any information from third parties? We do not collect any information from third parties.
              <br />
              <br />
              How do we process your information? We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We process your information only when we have a valid legal reason to do so.
              <br />
              <br />
              In what situations and with which types of parties do we share personal information? We may share information in specific situations and with specific categories of third parties.
              <br />
              <br />
              How do we keep your information safe? We have adequate organizational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure.
              <br />
              <br />
              What are your rights? Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information.
              <br />
              <br />
              How do you exercise your rights? The easiest way to exercise your rights is by visiting http://www.viewr.co, or by contacting us. We will consider and act upon any request in accordance with applicable data protection laws.
              <br />
              <br />
              Want to learn more about what we do with any information we collect? Review the Privacy Notice in full.
              <br />
              <br />
              <strong>TABLE OF CONTENTS</strong>
              <br />
              1. WHAT INFORMATION DO WE COLLECT?
              <br />
              2. HOW DO WE PROCESS YOUR INFORMATION?
              <br />
              3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?
              <br />
              4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
              <br />
              5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
              <br />
              6. HOW DO WE HANDLE YOUR SOCIAL LOGINS?
              <br />
              7. HOW LONG DO WE KEEP YOUR INFORMATION?
              <br />
              8. HOW DO WE KEEP YOUR INFORMATION SAFE?
              <br />
              9. DO WE COLLECT INFORMATION FROM MINORS?
              <br />
              10. WHAT ARE YOUR PRIVACY RIGHTS?
              <br />
              11. CONTROLS FOR DO-NOT-TRACK FEATURES
              <br />
              12. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
              <br />
              13. DO WE MAKE UPDATES TO THIS NOTICE?
              <br />
              14. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
              <br />
              15. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
              <br />
              <br />
              <strong>1. WHAT INFORMATION DO WE COLLECT?</strong>
              <br />
              <br />
              <strong>Personal information you disclose to us</strong>
              <br />
              In Short: We collect personal information that you provide to us.
              <br />
              <br />
              We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.
              <br />
              <br />
              Sensitive Information. We do not process sensitive information.
              <br />
              <br />
              Payment Data. We may collect data necessary to process your payment if you choose to make purchases, such as your payment instrument number, and the security code associated with your payment instrument. All payment data is handled and stored by Stripe. You may find their privacy notice link(s) here: https://stripe.com/privacy.
              <br />
              <br />
              Social Media Login Data. We may provide you with the option to register with us using your existing social media account details, like your Facebook, X, or other social media account. If you choose to register in this way, we will collect certain profile information about you from the social media provider, as described in the section called "HOW DO WE HANDLE YOUR SOCIAL LOGINS?" below.
              <br />
              <br />
              All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information.
              <br />
              <br />
              <strong>Information automatically collected</strong>
              <br />
              In Short: Some information — such as your Internet Protocol (IP) address and/or browser and device characteristics — is collected automatically when you visit our Services.
              <br />
              <br />
              We automatically collect certain information when you visit, use, or navigate the Services. This information does not reveal your specific identity (like your name or contact information) but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, location, information about how and when you use our Services, and other technical information. This information is primarily needed to maintain the security and operation of our Services, and for our internal analytics and reporting purposes.
              <br />
              <br />
              Like many businesses, we also collect information through cookies and similar technologies. You can find out more about this in our Cookie Notice: http://www.viewr.co.
              <br />
              <br />
              The information we collect includes:
              <br />
              <br />
              ● Log and Usage Data. Log and usage data is service-related, diagnostic, usage, and performance information our servers automatically collect when you access or use our Services and which we record in log files. Depending on how you interact with us, this log data may include your IP address, device information, browser type, and settings and information about your activity in the Services (such as the date/time stamps associated with your usage, pages and files viewed, searches, and other actions you take such as which features you use), device event information (such as system activity, error reports (sometimes called "crash dumps"), and hardware settings).
              <br />
              <br />
              ● Device Data. We collect device data such as information about your computer, phone, tablet, or other device you use to access the Services. Depending on the device used, this device data may include information such as your IP address (or proxy server), device and application identification numbers, location, browser type, hardware model, Internet service provider and/or mobile carrier, operating system, and system configuration information.
              <br />
              <br />
              ● Location Data. We collect location data such as information about your device's location, which can be either precise or imprecise. How much information we collect depends on the type and settings of the device you use to access the Services. For example, we may use GPS and other technologies to collect geolocation data that tells us your current location (based on your IP address). You can opt out of allowing us to collect this information either by refusing access to the information or by disabling your Location setting on your device. However, if you choose to opt out, you may not be able to use certain aspects of the Services.
              <br />
              <br />
              <strong>Google API</strong>
              <br />
              Our use of information received from Google APIs will adhere to Google API Services User Data Policy, including the Limited Use requirements.
              <br />
              <br />
              <strong>2. HOW DO WE PROCESS YOUR INFORMATION?</strong>
              <br />
              <br />
              In Short: We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We process the personal information for the following purposes listed below. We may also process your information for other purposes only with your prior explicit consent.
              <br />
              <br />
              We process your personal information for a variety of reasons, depending on how you interact with our Services, including:
              <br />
              <br />
              ● To facilitate account creation and authentication and otherwise manage user accounts. We may process your information so you can create and log in to your account, as well as keep your account in working order.
              <br />
              <br />
              ● To deliver and facilitate delivery of services to the user. We may process your information to provide you with the requested service.
              <br />
              <br />
              ● To respond to user inquiries/offer support to users. We may process your information to respond to your inquiries and solve any potential issues you might have with the requested service.
              <br />
              <br />
              ● To send administrative information to you. We may process your information to send you details about our products and services, changes to our terms and policies, and other similar information.
              <br />
              <br />
              ● To fulfill and manage your orders. We may process your information to fulfill and manage your orders, payments, returns, and exchanges made through the Services.
              <br />
              <br />
              ● To enable user-to-user communications. We may process your information if you choose to use any of our offerings that allow for communication with another user.
              <br />
              <br />
              ● To request feedback. We may process your information when necessary to request feedback and to contact you about your use of our Services.
              <br />
              <br />
              ● To send you marketing and promotional communications. We may process the personal information you send to us for our marketing purposes, if this is in accordance with your marketing preferences. You can opt out of our marketing emails at any time. For more information, see "WHAT ARE YOUR PRIVACY RIGHTS?" below.
              <br />
              <br />
              ● To protect our Services. We may process your information as part of our efforts to keep our Services safe and secure, including fraud monitoring and prevention.
              <br />
              <br />
              ● To identify usage trends. We may process information about how you use our Services to better understand how they are being used so we can improve them.
              <br />
              <br />
              ● To determine the effectiveness of our marketing and promotional campaigns. We may process your information to better understand how to provide marketing and promotional campaigns that are most relevant to you.
              <br />
              <br />
              ● To save or protect an individual's vital interest. We may process your information when necessary to save or protect an individual's vital interest, such as to prevent harm.
              <br />
              <br />
              <strong>3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?</strong>
              <br />
              <br />
              In Short: We only process your personal information when we believe it is necessary and we have a valid legal reason (i.e., legal basis) to do so under applicable law, like with your consent, to comply with laws, to provide you with services to enter into or fulfill our contractual obligations, to protect your rights, or to fulfill our legitimate business interests.
              <br />
              <br />
              If you are located in the EU or UK, this section applies to you. The General Data Protection Regulation (GDPR) and UK GDPR require us to explain the valid legal bases we rely on in order to process your personal information. As such, we may rely on the following legal bases to process your personal information:
              <br />
              <br />
              ● Consent. We may process your information if you have given us permission (i.e., consent) to use your personal information for a specific purpose. You can withdraw your consent at any time.
              <br />
              <br />
              ● Performance of a Contract. We may process your personal information when we believe it is necessary to fulfill our contractual obligations to you, including providing our Services or at your request prior to entering into a contract with you.
              <br />
              <br />
              ● Legitimate Interests. We may process your information when we believe it is reasonably necessary to achieve our legitimate business interests and those interests do not outweigh your interests and fundamental rights and freedoms. For example, we may process your personal information for some of the purposes described in order to:
              <br />
              <br />
              ● Send users information about special offers and discounts on our products and services
              <br />
              ● Analyze how our Services are used so we can improve them to engage and retain users
              <br />
              ● Support our marketing activities
              <br />
              ● Diagnose problems and/or prevent fraudulent activities
              <br />
              ● Understand how our users use our products and services so we can improve user experience
              <br />
              <br />
              ● Legal Obligations. We may process your information where we believe it is necessary for compliance with our legal obligations, such as to cooperate with a law enforcement body or regulatory agency, exercise or defend our legal rights, or disclose your information as evidence in litigation in which we are involved.
              <br />
              <br />
              ● Vital Interests. We may process your information where we believe it is necessary to protect your vital interests or the vital interests of a third party, such as situations involving potential threats to the safety of any person.
              <br />
              <br />
              If you are located in Canada, this section applies to you. We may process your information if you have given us specific permission (i.e., express consent) to use your personal information for a specific purpose, or in situations where your permission can be inferred (i.e., implied consent). You can withdraw your consent at any time. In some exceptional cases, we may be legally permitted under applicable law to process your information without your consent.
              <br />
              <br />
              <strong>4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</strong>
              <br />
              <br />
              In Short: We may share information in specific situations described in this section and/or with the following categories of third parties.
              <br />
              <br />
              Vendors, Consultants, and Other Third-Party Service Providers. We may share your data with third-party vendors, service providers, contractors, or agents ("third parties") who perform services for us or on our behalf and require access to such information to do that work. We have contracts in place with our third parties, which are designed to help safeguard your personal information. This means that they cannot do anything with your personal information unless we have instructed them to do it. They will also not share your personal information with any organization apart from us. They also commit to protect the data they hold on our behalf and to retain it for the period we instruct.
              <br />
              <br />
              The categories of third parties we may share personal information with are as follows:
              <br />
              <br />
              ● Cloud Computing Services
              <br />
              ● Data Analytics Services
              <br />
              ● Communication & Collaboration Tools
              <br />
              ● User Account Registration & Authentication Services
              <br />
              ● Website Hosting Service Providers
              <br />
              ● Performance Monitoring Tools
              <br />
              ● Product Engineering & Design Tools
              <br />
              ● Data Storage Service Providers
              <br />
              ● Payment Processors
              <br />
              <br />
              We also may need to share your personal information in the following situations:
              <br />
              <br />
              ● Business Transfers. We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
              <br />
              <br />
              <strong>5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</strong>
              <br />
              <br />
              In Short: We may use cookies and other tracking technologies to collect and store your information.
              <br />
              <br />
              We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information when you interact with our Services. Some online tracking technologies help us maintain the security of our Services and your account, prevent crashes, fix bugs, save your preferences, and assist with basic site functions.
              <br />
              <br />
              We also permit third parties and service providers to use online tracking technologies on our Services for analytics and advertising. To the extent these online tracking technologies are deemed to be a "sale"/"sharing" (which includes targeted advertising, as defined under the applicable laws) under applicable US state laws, you can opt out of these online tracking technologies by submitting a request as described below under section "DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?"
              <br />
              <br />
              Specific information about how we use such technologies and how you can refuse certain cookies is set out in our Cookie Notice: http://www.viewr.co.
              <br />
              <br />
              <strong>Google Analytics</strong>
              <br />
              We may share your information with Google Analytics to track and analyze the use of the Services. To opt out of being tracked by Google Analytics across the Services, visit https://tools.google.com/dlpage/gaoptout. For more information on the privacy practices of Google, please visit the Google Privacy & Terms page.
              <br />
              <br />
              <strong>6. HOW DO WE HANDLE YOUR SOCIAL LOGINS?</strong>
              <br />
              <br />
              In Short: If you choose to register or log in to our Services using a social media account, we may have access to certain information about you.
              <br />
              <br />
              Our Services offer you the ability to register and log in using your third-party social media account details (like your Facebook or X logins). Where you choose to do this, we will receive certain profile information about you from your social media provider. The profile information we receive may vary depending on the social media provider concerned, but will often include your name, email address, friends list, and profile picture, as well as other information you choose to make public on such a social media platform.
              <br />
              <br />
              We will use the information we receive only for the purposes that are described in this Privacy Notice or that are otherwise made clear to you on the relevant Services. Please note that we do not control, and are not responsible for, other uses of your personal information by your third-party social media provider. We recommend that you review their privacy notice to understand how they collect, use, and share your personal information, and how you can set your privacy preferences on their sites and apps.
              <br />
              <br />
              <strong>7. HOW LONG DO WE KEEP YOUR INFORMATION?</strong>
              <br />
              <br />
              In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy Notice unless otherwise required by law.
              <br />
              <br />
              We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements). No purpose in this notice will require us keeping your personal information for longer than the period of time in which users have an account with us.
              <br />
              <br />
              When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.
              <br />
              <br />
              <strong>8. HOW DO WE KEEP YOUR INFORMATION SAFE?</strong>
              <br />
              <br />
              In Short: We aim to protect your personal information through a system of organizational and technical security measures.
              <br />
              <br />
              We have implemented appropriate and reasonable technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Although we will do our best to protect your personal information, transmission of personal information to and from our Services is at your own risk. You should only access the Services within a secure environment.
              <br />
              <br />
              <strong>9. DO WE COLLECT INFORMATION FROM MINORS?</strong>
              <br />
              <br />
              In Short: We do not knowingly collect data from or market to children under 18 years of age or the equivalent age as specified by law in your jurisdiction.
              <br />
              <br />
              We do not knowingly collect, solicit data from, or market to children under 18 years of age or the equivalent age as specified by law in your jurisdiction, nor do we knowingly sell such personal information. By using the Services, you represent that you are at least 18 or the equivalent age as specified by law in your jurisdiction or that you are the parent or guardian of such a minor and consent to such minor dependent's use of the Services. If we learn that personal information from users less than 18 years of age or the equivalent age as specified by law in your jurisdiction has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records. If you become aware of any data we may have collected from children under age 18 or the equivalent age as specified by law in your jurisdiction, please contact us at sales@byond-r.com.
              <br />
              <br />
              <strong>10. WHAT ARE YOUR PRIVACY RIGHTS?</strong>
              <br />
              <br />
              In Short: Depending on your state of residence in the US or in some regions, such as the European Economic Area (EEA), United Kingdom (UK), Switzerland, and Canada, you have rights that allow you greater access to and control over your personal information. You may review, change, or terminate your account at any time, depending on your country, province, or state of residence.
              <br />
              <br />
              In some regions (like the EEA, UK, Switzerland, and Canada), you have certain rights under applicable data protection laws. These may include the right (i) to request access and obtain a copy of your personal information, (ii) to request rectification or erasure; (iii) to restrict the processing of your personal information; (iv) if applicable, to data portability; and (v) not to be subject to automated decision-making. You can make such a request by contacting us by using the contact details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below. We will consider and act upon any request in accordance with applicable data protection laws.
              <br />
              <br />
              If you are located in the EEA or UK and you believe we are unlawfully processing your personal information, you also have the right to complain to your Member State data protection authority or UK data protection authority. If you are located in Switzerland, you may contact the Federal Data Protection and Information Commissioner.
              <br />
              <br />
              Withdrawing your consent: If we are relying on your consent to process your personal information, you have the right to withdraw your consent at any time. You can withdraw your consent at any time by contacting us by using the contact details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below or updating your preferences.
              <br />
              <br />
              Opting out of marketing and promotional communications: You can unsubscribe from our marketing and promotional communications at any time by clicking on the unsubscribe link in the emails that we send, or by contacting us using the details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below. You will then be removed from the marketing lists. However, we may still communicate with you — for example, to send you service-related messages that are necessary for the administration and use of your account, to respond to service requests, or for other non-marketing purposes.
              <br />
              <br />
              <strong>Account Information</strong>
              <br />
              If you would at any time like to review or change the information in your account or terminate your account, you can:
              <br />
              <br />
              ● Log in to your account settings and update your user account.
              <br />
              <br />
              Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases. However, we may retain some information in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with applicable legal requirements.
              <br />
              <br />
              Cookies and similar technologies: Most Web browsers are set to accept cookies by default. If you prefer, you can usually choose to set your browser to remove cookies and to reject cookies. If you choose to remove cookies or reject cookies, this could affect certain features or services of our Services. For further information, please see our Cookie Notice: http://www.viewr.co.
              <br />
              <br />
              If you have questions or comments about your privacy rights, you may email us at sales@byond-r.com.
              <br />
              <br />
              <strong>11. CONTROLS FOR DO-NOT-TRACK FEATURES</strong>
              <br />
              <br />
              Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage, no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will inform you about that practice in a revised version of this Privacy Notice.
              <br />
              <br />
              California law requires us to let you know how we respond to web browser DNT signals. Because there currently is not an industry or legal standard for recognizing or honoring DNT signals, we do not respond to them at this time.
              <br />
              <br />
              <strong>12. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</strong>
              <br />
              <br />
              In Short: If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the right to request access to and receive details about the personal information we maintain about you and how we have processed it, correct inaccuracies, get a copy of, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law.
              <br />
              <br />
              Categories of Personal Information We Collect: The table below shows the categories of personal information we have collected in the past twelve (12) months. Category A (Identifiers), B (Personal information as defined in the California Customer Records statute), F (Internet or other similar network activity), and G (Geolocation data) - YES. Categories C, D, E, H, I, J, K, L - NO.
              <br />
              <br />
              We may also collect other personal information through instances where you interact with us in person, online, or by phone or mail in the context of: Receiving help through our customer support channels; Participation in customer surveys or contests; and Facilitation in the delivery of our Services and to respond to your inquiries.
              <br />
              <br />
              Your Rights: You have rights under certain US state data protection laws. These rights include: Right to know whether or not we are processing your personal data; Right to access your personal data; Right to correct inaccuracies in your personal data; Right to request the deletion of your personal data; Right to obtain a copy of the personal data you previously shared with us; Right to non-discrimination for exercising your rights; Right to opt out of the processing of your personal data if it is used for targeted advertising, the sale of personal data, or profiling.
              <br />
              <br />
              How to Exercise Your Rights: To exercise these rights, you can contact us by visiting http://www.viewr.co, by emailing us at sales@byond-r.com, or by referring to the contact details at the bottom of this document.
              <br />
              <br />
              We have not sold or shared any personal information to third parties for a business or commercial purpose in the preceding twelve (12) months.
              <br />
              <br />
              California "Shine The Light" Law: California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits our users who are California residents to request and obtain from us, once a year and free of charge, information about categories of personal information (if any) we disclosed to third parties for direct marketing purposes and the names and addresses of all third parties with which we shared personal information in the immediately preceding calendar year. If you are a California resident and would like to make such a request, please submit your request in writing to us by using the contact details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"
              <br />
              <br />
              <strong>13. DO WE MAKE UPDATES TO THIS NOTICE?</strong>
              <br />
              <br />
              In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.
              <br />
              <br />
              We may update this Privacy Notice from time to time. The updated version will be indicated by an updated "Revised" date at the top of this Privacy Notice. If we make material changes to this Privacy Notice, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this Privacy Notice frequently to be informed of how we are protecting your information.
              <br />
              <br />
              <strong>14. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</strong>
              <br />
              <br />
              If you have questions or comments about this notice, you may contact our Data Protection Officer (DPO) by email at sales@byond-r.com, by phone at 1-213-249-1905, or contact us by post at:
              <br />
              <br />
              Byond R Inc.
              <br />
              Data Protection Officer
              <br />
              1201, 21, Hwangseul-ro360bungil, Bungdang-gu
              <br />
              Seongnam-si, Gyeonggi-do 13591
              <br />
              South Korea
              <br />
              <br />
              <strong>15. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</strong>
              <br />
              <br />
              You have the right to request access to the personal information we collect from you, details about how we have processed it, correct inaccuracies, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law. To request to review, update, or delete your personal information, please visit: http://www.viewr.co.
            </Typography>
          </CustomScrollbar>
        </Box>

        {/* Bottom Spacer */}
        <Box
          sx={{
            height: {
              xs: designTokens.numbers.gap[16],
              sm: designTokens.numbers.gap[24],
              md: designTokens.numbers.gap[32],
            },
            flexShrink: 0,
          }}
        />
      </Box>
    </Dialog>
  );
};

export default Privacy;
